import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { NAV_CATS } from "@/lib/constants";
import { LogOut, Settings, ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTheme } from "@/context/ThemeContext";
import GlobalSearch from "@/components/GlobalSearch";

const BADGE_LINKS = { devis: "/app/devis?filtre=impayees" };
const ICON_BTN = "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-400 transition-colors hover:border-[#FF6A00]/40 hover:text-[#FF6A00]";

export function BrandMark() {
  const { theme } = useTheme();
  return <img src={theme === "dark" ? "/Logo_Full_White.webp" : "/Logo_Full_Black.webp"} alt="HalgarOS" className="h-10 w-auto" />;
}

function NavBadge({ count, compact }) {
  if (!count) return null;
  if (compact) return <span data-testid="nav-badge-dot" className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#FF6A00] ring-2 ring-zinc-950" />;
  return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-md bg-[#FF6A00]/15 px-1.5 text-[10px] font-semibold text-[#FF6A00]">{count > 9 ? "9+" : count}</span>;
}

function NavItem({ n, compact, onNavigate, count }) {
  const navigate = useNavigate();
  const badgeTo = BADGE_LINKS[n.page];
  const onBadge = (e) => { if (!badgeTo) return; e.preventDefault(); e.stopPropagation(); navigate(badgeTo); onNavigate?.(); };
  const cls = ({ isActive }) =>
    `group relative flex h-10 items-center rounded-xl text-sm font-medium transition-colors duration-200 ${compact ? "justify-center px-0" : "gap-3 px-3"} ${
      isActive ? "bg-[#FF6A00]/12 text-[#FF6A00]" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-white"
    }`;
  const link = (
    <NavLink to={n.to} end={n.end} className={cls} onClick={onNavigate} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
      {!compact && <span className="truncate">{n.label}</span>}
      {count > 0 && <span data-testid={`nav-badge-${n.page}`} onClick={onBadge} title={badgeTo ? "Voir les factures impayées" : undefined} className={`${compact ? "" : "ml-auto"} ${badgeTo ? "cursor-pointer transition-transform hover:scale-110" : ""}`}><NavBadge count={count} compact={compact} /></span>}
    </NavLink>
  );
  if (!compact) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="border-zinc-800 bg-zinc-900 text-white">{n.label}{count > 0 ? ` (${count})` : ""}</TooltipContent>
    </Tooltip>
  );
}

function NavSection({ cat, items, compact, collapsed, onToggle, onNavigate, counters }) {
  return (
    <div className="space-y-1 pt-5">
      {compact ? <div className="mx-auto mb-2 h-px w-6 bg-zinc-800" /> : (
        <button type="button" onClick={onToggle} data-testid={`nav-cat-${cat.key}`}
          className="flex w-full items-center justify-between px-3 pb-1.5 text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-300">
          <span>{cat.label}</span>
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      )}
      {!collapsed && items.map((n) => <NavItem key={n.to} n={n} compact={compact} onNavigate={onNavigate} count={counters[n.page]} />)}
    </div>
  );
}

function UserFooter({ user, org, compact, onAccount, onLogout }) {
  const initial = (user?.name || "?").slice(0, 1).toUpperCase();
  const logoutBtn = (
    <button data-testid="logout-btn" onClick={onLogout} title="Déconnexion" className="text-zinc-500 hover:text-red-400">
      <LogOut className="h-[18px] w-[18px]" strokeWidth={1.6} />
    </button>
  );
  if (compact) {
    return (
      <div className="flex flex-col items-center gap-3">
        <button data-testid="account-btn" onClick={onAccount} title="Mon compte" className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-sm font-semibold text-white transition-colors hover:bg-[#FF6A00]">{initial}</button>
        {logoutBtn}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 px-1">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-sm font-semibold text-white">{initial}</div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-white">{user?.name}</p>
        <p className="truncate text-xs text-zinc-500">{org?.name}</p>
      </div>
      <button data-testid="account-btn" onClick={onAccount} className="text-zinc-500 transition-colors hover:text-[#FF6A00]" title="Mon compte">
        <Settings className="h-[18px] w-[18px]" strokeWidth={1.6} />
      </button>
      {logoutBtn}
    </div>
  );
}

export function Sidebar({ nav, counters, compact = false, mobile = false, isCatCollapsed, onToggleCat, onToggleCompact, onNavigate, user, org, onAccount, onLogout }) {
  return (
    <TooltipProvider delayDuration={100}>
      <div className={`flex items-center pb-5 pt-1 ${compact ? "flex-col gap-3" : "justify-between gap-2 px-1"}`}>
        {compact ? <img src="/halgaros-icon-128.png" alt="HalgarOS" className="h-9 w-9 object-contain" /> : <BrandMark />}
        {!mobile && (
          <button data-testid="sidebar-compact-toggle" onClick={onToggleCompact} className={ICON_BTN} title={compact ? "Déplier le menu" : "Réduire le menu"}>
            {compact ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </div>
      <div className="pb-5">
        <GlobalSearch variant="sidebar" compact={compact} onExpand={onToggleCompact} />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto no-scrollbar">
        {nav.filter((n) => n.cat === "home").map((n) => <NavItem key={n.to} n={n} compact={compact} onNavigate={onNavigate} count={counters[n.page]} />)}
        {NAV_CATS.map((c) => {
          const items = nav.filter((n) => n.cat === c.key);
          if (!items.length) return null;
          return <NavSection key={c.key} cat={c} items={items} compact={compact} collapsed={!compact && isCatCollapsed(c.key)}
            onToggle={() => onToggleCat(c.key)} onNavigate={onNavigate} counters={counters} />;
        })}
      </nav>
      <div className="border-t border-zinc-800 pt-4">
        <UserFooter user={user} org={org} compact={compact} onAccount={onAccount} onLogout={onLogout} />
      </div>
    </TooltipProvider>
  );
}
